/**
 * Contratos de comerciante — o que a Pagali cobra por pagamento na sua rede.
 *
 * O utilizador não paga taxa ao pagar; quem paga é o comerciante, por ter o
 * cashless. Este ecrã é o que permite decidir quanto, antes de se começar a
 * cobrar: taxa por comerciante, escalões por volume mensal, valor fixo por
 * transacção, ou isenção.
 *
 * Publicar cria sempre uma VERSÃO NOVA. A anterior fica como histórico, para se
 * poder explicar meses depois porque é que a comissão de Março foi diferente da
 * de Abril.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Snackbar, Grid2 as Grid, Divider,
  FormControlLabel, Switch, Tooltip, CircularProgress, InputAdornment,
} from '@mui/material';
import {
  Storefront, Add, Delete, Edit, Refresh, Calculate, Percent,
} from '@mui/icons-material';
import api from '../../../services/api';

interface Comerciante {
  entityId: string;
  nome: string;
  codigo: string;
  temContratoProprio: boolean;
  isento: boolean;
  rateBps: number;
  fixedFeeCVE: number;
  vatRateBps: number;
  escaloes: number;
}

interface Escalao {
  minVolumeCVE: number | string;
  maxVolumeCVE: number | string | null;
  rateBps: number | string;
  fixedFeeCVE: number | string;
}

const bpsParaPct = (bps: number) => (Number(bps) / 100);
const pctParaBps = (pct: number | string) => Math.round(Number(pct) * 100);

const ContratosComerciante: React.FC = () => {
  const [lista, setLista] = useState<Comerciante[]>([]);
  const [porOmissao, setPorOmissao] = useState<any>(null);
  const [aCarregar, setACarregar] = useState(true);
  const [procura, setProcura] = useState('');
  const [toast, setToast] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false, msg: '', sev: 'success',
  });

  // Diálogo de edição
  const [aberto, setAberto] = useState(false);
  const [alvo, setAlvo] = useState<Comerciante | null>(null);   // null = contrato por omissão
  const [isento, setIsento] = useState(false);
  const [taxaPct, setTaxaPct] = useState<string>('6');
  const [fixo, setFixo] = useState<string>('0');
  const [ivaPct, setIvaPct] = useState<string>('15');
  const [escaloes, setEscaloes] = useState<Escalao[]>([]);
  const [notas, setNotas] = useState('');
  const [aGuardar, setAGuardar] = useState(false);
  const [simulacao, setSimulacao] = useState<any[] | null>(null);

  const mostrar = (msg: string, sev: 'success' | 'error') => setToast({ open: true, msg, sev });

  const carregar = useCallback(async () => {
    setACarregar(true);
    try {
      const r = await api.get('/admin/merchant-contracts');
      setLista(r.data.data || []);
      setPorOmissao(r.data.porOmissao || null);
    } catch (e: any) {
      mostrar(e?.response?.data?.error || 'Não foi possível carregar os contratos.', 'error');
    } finally {
      setACarregar(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = async (c: Comerciante | null) => {
    setAlvo(c);
    setSimulacao(null);
    setNotas('');
    if (c) {
      setIsento(c.isento);
      setTaxaPct(String(bpsParaPct(c.rateBps)));
      setFixo(String(c.fixedFeeCVE));
      setIvaPct(String(bpsParaPct(c.vatRateBps)));
      // Os escalões vêm do detalhe — a lista só traz a contagem.
      try {
        const r = await api.get(`/admin/merchant-contracts/${c.entityId}`);
        setEscaloes((r.data.emVigor?.escaloes || []).map((e: any) => ({
          minVolumeCVE: e.minVolumeCVE, maxVolumeCVE: e.maxVolumeCVE,
          rateBps: bpsParaPct(e.rateBps), fixedFeeCVE: e.fixedFeeCVE,
        })));
      } catch { setEscaloes([]); }
    } else {
      setIsento(false);
      setTaxaPct(String(bpsParaPct(porOmissao?.rateBps ?? 600)));
      setFixo(String(porOmissao?.fixedFeeCVE ?? 0));
      setIvaPct(String(bpsParaPct(porOmissao?.vatRateBps ?? 1500)));
      setEscaloes([]);
    }
    setAberto(true);
  };

  const simular = async () => {
    if (!alvo) return;
    try {
      const r = await api.post('/admin/merchant-contracts/simular', {
        entityId: alvo.entityId, valores: [100, 1000, 5000],
      });
      setSimulacao(r.data.data || []);
    } catch (e: any) {
      mostrar(e?.response?.data?.error || 'Não foi possível simular.', 'error');
    }
  };

  const publicar = async () => {
    setAGuardar(true);
    try {
      await api.post('/admin/merchant-contracts', {
        entityId: alvo?.entityId ?? null,
        isento,
        rateBps: pctParaBps(taxaPct),
        fixedFeeCVE: Number(fixo) || 0,
        vatRateBps: pctParaBps(ivaPct),
        notas: notas || null,
        escaloes: escaloes.map((e) => ({
          minVolumeCVE: Number(e.minVolumeCVE) || 0,
          maxVolumeCVE: e.maxVolumeCVE === '' || e.maxVolumeCVE == null ? null : Number(e.maxVolumeCVE),
          rateBps: pctParaBps(e.rateBps),
          fixedFeeCVE: Number(e.fixedFeeCVE) || 0,
        })),
      });
      mostrar(alvo ? `Contrato de ${alvo.nome} publicado.` : 'Contrato base publicado.', 'success');
      setAberto(false);
      carregar();
    } catch (e: any) {
      mostrar(e?.response?.data?.error || 'Não foi possível publicar.', 'error');
    } finally {
      setAGuardar(false);
    }
  };

  const filtrados = lista.filter((c) =>
    !procura || c.nome?.toLowerCase().includes(procura.toLowerCase())
    || c.codigo?.toLowerCase().includes(procura.toLowerCase()));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>Contratos de Comerciante</Typography>
          <Typography variant="body1" color="text.secondary">
            Quanto a Pagali cobra por cada pagamento na sua rede. O utilizador não paga
            taxa ao pagar — quem paga é o comerciante, por ter o cashless.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
          <Button variant="contained" startIcon={<Percent />} onClick={() => abrir(null)}>
            Contrato base
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Storefront color="primary" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">Contrato base da plataforma</Typography>
            <Typography variant="h6">
              {porOmissao ? `${bpsParaPct(porOmissao.rateBps)}% + IVA ${bpsParaPct(porOmissao.vatRateBps)}%` : '—'}
              {porOmissao?.fixedFeeCVE > 0 && ` + ${porOmissao.fixedFeeCVE} CVE por transacção`}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Aplica-se a quem não tem contrato próprio
          </Typography>
        </CardContent>
      </Card>

      <TextField
        size="small" fullWidth placeholder="Procurar comerciante…" sx={{ mb: 2 }}
        value={procura} onChange={(e) => setProcura(e.target.value)}
      />

      {aCarregar ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Comerciante</TableCell>
                  <TableCell>Taxa aplicada</TableCell>
                  <TableCell>Fixo/transacção</TableCell>
                  <TableCell>Escalões</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map((c) => (
                  <TableRow key={c.entityId} hover>
                    <TableCell>
                      <Typography variant="body2">{c.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.codigo}</Typography>
                    </TableCell>
                    <TableCell>
                      {c.isento
                        ? <Chip label="Isento" size="small" color="success" />
                        : <Typography variant="body2">{bpsParaPct(c.rateBps)}% + IVA {bpsParaPct(c.vatRateBps)}%</Typography>}
                    </TableCell>
                    <TableCell>{c.fixedFeeCVE > 0 ? `${c.fixedFeeCVE} CVE` : '—'}</TableCell>
                    <TableCell>{c.escaloes > 0 ? <Chip label={`${c.escaloes} escalões`} size="small" variant="outlined" /> : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small" variant="outlined"
                        label={c.temContratoProprio ? 'Contrato próprio' : 'Base'}
                        color={c.temContratoProprio ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Configurar">
                        <IconButton size="small" onClick={() => abrir(c)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtrados.length === 0 && (
                  <TableRow><TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhum comerciante encontrado.
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {/* ── Configuração ─────────────────────────────────────────────────── */}
      <Dialog open={aberto} onClose={() => setAberto(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {alvo ? `Contrato — ${alvo.nome}` : 'Contrato base da plataforma'}
        </DialogTitle>
        <DialogContent dividers>
          {!alvo && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Isto muda a taxa de <strong>todos</strong> os comerciantes que não tenham
              contrato próprio.
            </Alert>
          )}

          <FormControlLabel
            control={<Switch checked={isento} onChange={(e) => setIsento(e.target.checked)} />}
            label="Isento de comissão"
            sx={{ mb: 1 }}
          />
          {isento && (
            <Alert severity="info" sx={{ mb: 2 }}>
              O comerciante recebe o valor integral de cada pagamento.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small" label="Taxa" type="number" disabled={isento}
                value={taxaPct} onChange={(e) => setTaxaPct(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small" label="Fixo por transacção" type="number" disabled={isento}
                value={fixo} onChange={(e) => setFixo(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">CVE</InputAdornment> }}
                helperText="Somado à percentagem"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small" label="IVA sobre a comissão" type="number" disabled={isento}
                value={ivaPct} onChange={(e) => setIvaPct(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2">Escalões por volume mensal</Typography>
              <Typography variant="caption" color="text.secondary">
                Quando existem, substituem a taxa acima conforme o que o comerciante
                vendeu no mês. Sem escalões, aplica-se sempre a taxa acima.
              </Typography>
            </Box>
            <Button
              size="small" startIcon={<Add />} disabled={isento}
              onClick={() => setEscaloes([...escaloes, { minVolumeCVE: 0, maxVolumeCVE: '', rateBps: taxaPct, fixedFeeCVE: 0 }])}
            >
              Escalão
            </Button>
          </Box>

          {escaloes.map((e, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
              <Grid size={{ xs: 3 }}>
                <TextField size="small" fullWidth label="De (CVE)" type="number" value={e.minVolumeCVE}
                  onChange={(ev) => { const c = [...escaloes]; c[i].minVolumeCVE = ev.target.value; setEscaloes(c); }} />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <TextField size="small" fullWidth label="Até (CVE)" type="number" value={e.maxVolumeCVE ?? ''}
                  placeholder="sem limite"
                  onChange={(ev) => { const c = [...escaloes]; c[i].maxVolumeCVE = ev.target.value; setEscaloes(c); }} />
              </Grid>
              <Grid size={{ xs: 2.5 }}>
                <TextField size="small" fullWidth label="Taxa %" type="number" value={e.rateBps}
                  onChange={(ev) => { const c = [...escaloes]; c[i].rateBps = ev.target.value; setEscaloes(c); }} />
              </Grid>
              <Grid size={{ xs: 2.5 }}>
                <TextField size="small" fullWidth label="Fixo CVE" type="number" value={e.fixedFeeCVE}
                  onChange={(ev) => { const c = [...escaloes]; c[i].fixedFeeCVE = ev.target.value; setEscaloes(c); }} />
              </Grid>
              <Grid size={{ xs: 1 }}>
                <IconButton size="small" onClick={() => setEscaloes(escaloes.filter((_, j) => j !== i))}>
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth size="small" label="Notas" value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ex.: acordado na reunião de 12/08, válido enquanto o volume se mantiver"
          />

          {alvo && (
            <Box sx={{ mt: 2 }}>
              <Button size="small" startIcon={<Calculate />} onClick={simular}>
                Simular com o contrato em vigor
              </Button>
              {simulacao && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pagamento</TableCell><TableCell>Comissão</TableCell>
                      <TableCell>IVA</TableCell><TableCell>Comerciante recebe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {simulacao.map((l: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{l.valor} CVE</TableCell>
                        <TableCell>{l.erro ? '—' : l.commissionNetCVE}</TableCell>
                        <TableCell>{l.erro ? '—' : l.vatCVE}</TableCell>
                        <TableCell>{l.erro ? l.erro : `${l.toVendorCVE} CVE`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                A simulação usa o contrato que está em vigor, não o que está no formulário.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={publicar} disabled={aGuardar}>
            {aGuardar ? 'A publicar…' : 'Publicar versão'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open} autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast({ ...toast, open: false })}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContratosComerciante;
